import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AlertCircle, Check, Loader2, Paperclip, Search, X } from "lucide-react";
import * as React from "react";
import { formatAttachmentSize as formatChipSize } from "../../lib/attachmentFormat";
import { cn } from "../../lib/cn";

/**
 * EmailAttachmentField — the ONE shared email-attachment surface (Jul 2026).
 *
 * The single source of truth for staging email attachments across every Ando
 * compose surface: the HITL ``send_email`` action form (stacked
 * ``ActionFieldEditor`` ``file`` field), the "Nuova email" send preview, AND
 * the inbox reply composer. Each host wires its own data model but shares the
 * EXACT same minimal "pencil" UI (hairline chips, Lucide icons, a Paperclip
 * device-upload button, and the "Scegli dai documenti Ando" picker).
 *
 * Fully CONTROLLED — ``value`` + ``onChange`` are props, so the host owns the
 * persisted ``AttachmentChipValue[]`` (reply local state vs action
 * ``commit(field,…)``). The component owns ONLY the transient staging state
 * (files still encoding / uploading), keeping half-baked rows out of the
 * committed value until they're ``ready``.
 *
 * DRY anchor — see ``ActionFieldEditor``'s ``file`` renderer (thin wrapper) and
 * ``ReplyComposeSection`` (both delegate here).
 */

// ============================================================================
// Types (single source of truth — re-exported by ActionFieldEditor for the
// action pipeline, and surfaced from ``@ando/ui`` via ./email + ./index).
// ============================================================================

/**
 * One staged attachment. The shape mirrors the ``EmailAttachmentInput``
 * contract in ``@ando/core`` plus ``size_bytes`` for the chip display — kept
 * structural here so ``@ando/ui`` doesn't take a build-time dep on the core
 * API package.
 */
export interface AttachmentChipValue {
  readonly filename: string;
  readonly mime_type?: string;
  readonly size_bytes?: number;
  /** Inline base64 transport (small files ≤25 MB). */
  readonly content_b64?: string;
  /** MinIO temp object key (large files / two-step upload). */
  readonly minio_key?: string;
  /**
   * Reference to a document ALREADY in the user's Ando space (picked from the
   * "Scegli dai documenti Ando" picker). The BE resolves it to bytes at send
   * time (``_execute_send_email`` → ``fetch_email_attachment_payload``), so no
   * upload/transport is needed. Mutually exclusive with ``content_b64`` /
   * ``minio_key`` in practice.
   */
  readonly content_item_id?: string;
}

/**
 * One attachable document surfaced by the Ando-document picker. Mirrors the
 * ``AttachableFile`` wire shape from ``@ando/core`` (``dedup_id`` = the
 * canonical content_item id) but kept structural so ``@ando/ui`` takes no
 * build-time dep on the core API package.
 */
export interface AttachableDocumentOption {
  readonly contentItemId: string;
  readonly filename: string;
  readonly mimeType?: string | null;
  readonly sizeBytes?: number | null;
  readonly sourceLabel?: string | null;
}

/**
 * Search transport for the Ando-document attachment picker. Host supplies it
 * (wraps ``searchAttachableFiles`` from ``@ando/core``) so ``@ando/ui`` stays
 * API-client-free. ``query`` empty ⇒ recent files.
 */
export type SearchAttachableDocuments = (args: {
  readonly query: string;
  readonly signal?: AbortSignal;
}) => Promise<readonly AttachableDocumentOption[]>;

/**
 * Upload transport for staged attachments. The host supplies it so the UI
 * package stays free of API client deps. Receives the picked file + an optional
 * progress callback and must return a ready-to-attach payload (either inline
 * base64 OR a MinIO ref). Mirrors the contract of ``buildEmailAttachment`` in
 * ``@ando/core/api/endpoints/emailSend``.
 */
export type UploadAttachment = (
  file: File,
  options?: { readonly onProgress?: (fraction: number) => void },
) => Promise<AttachmentChipValue>;

/** Runtime narrowing for an ``AttachmentChipValue[]`` field value. */
export function isAttachmentArray(value: unknown): value is AttachmentChipValue[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === "object" &&
        v !== null &&
        "filename" in v &&
        ("content_b64" in v || "minio_key" in v || "content_item_id" in v),
    )
  );
}

interface StagingEntry {
  readonly id: string;
  readonly file: File;
  readonly status: "preparing" | "ready" | "error";
  readonly progress?: number;
  readonly error?: string;
  readonly payload?: AttachmentChipValue;
}

export interface EmailAttachmentFieldProps {
  /** Persisted (committed) attachments — controlled by the host. */
  readonly value: readonly AttachmentChipValue[];
  /** Emits the full next attachment list (persisted + newly-ready staged). */
  readonly onChange: (next: AttachmentChipValue[]) => void;
  /**
   * Upload transport for device files. When omitted the "Carica file dal
   * dispositivo" button is hidden (existing attachments stay visible).
   */
  readonly uploadAttachment?: UploadAttachment;
  /**
   * Search transport for the "Scegli dai documenti Ando" picker. When omitted,
   * only device upload is offered.
   */
  readonly searchAttachableDocuments?: SearchAttachableDocuments;
  /** Read-only mode — hides add controls, keeps chips (minus the ✕). */
  readonly disabled?: boolean;
  readonly className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const EmailAttachmentField: React.FC<EmailAttachmentFieldProps> = ({
  value,
  onChange,
  uploadAttachment,
  searchAttachableDocuments,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const ready: readonly AttachmentChipValue[] = value;
  const [staging, setStaging] = React.useState<readonly StagingEntry[]>([]);
  // Specchio sincrono di ``staging``: gli upload concorrenti hanno bisogno
  // dell'ultima lista SUBITO, e leggerla dall'updater di setState significava
  // chiamare ``onChange`` (setState del parent) durante il render.
  const stagingRef = React.useRef<readonly StagingEntry[]>([]);
  const applyStaging = React.useCallback(
    (updater: (prev: readonly StagingEntry[]) => readonly StagingEntry[]) => {
      const next = updater(stagingRef.current);
      stagingRef.current = next;
      setStaging(next);
      return next;
    },
    [],
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // Ando-document picker state — DRY: searches the SAME attachable-files index
  // the agent uses, so the user can attach a doc already in their space (a fork
  // they just made, a Drive file, a prior upload) without re-uploading it.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pickerQuery, setPickerQuery] = React.useState("");
  const [pickerResults, setPickerResults] = React.useState<readonly AttachableDocumentOption[]>([]);
  const [pickerLoading, setPickerLoading] = React.useState(false);
  const pickerInputRef = React.useRef<HTMLInputElement>(null);

  // Commit helper — combines the persisted ``ready`` chips with the locally
  // staged ones in ``ready`` state. The committed value MUST stay an
  // ``AttachmentChipValue[]``; staging entries that are still preparing are
  // kept out of the committed value so the host's diff-vs-proposed doesn't see
  // half-baked rows.
  const commit = React.useCallback(
    (nextReady: readonly AttachmentChipValue[], nextStaging: readonly StagingEntry[]) => {
      const stagedReady = nextStaging
        .filter(
          (s): s is StagingEntry & { payload: AttachmentChipValue } =>
            s.status === "ready" && Boolean(s.payload),
        )
        .map((s) => s.payload);
      onChange([...nextReady, ...stagedReady]);
    },
    [onChange],
  );

  const removeReady = (filename: string) => {
    const nextReady = ready.filter((r) => r.filename !== filename);
    // Va tolta anche dallo staging: ``commit`` ricompone il valore includendo
    // le entry ready, quindi altrimenti il file rimosso rientrerebbe subito.
    const nextStaging = applyStaging((prev) =>
      prev.filter((s) => !(s.status === "ready" && s.payload?.filename === filename)),
    );
    commit(nextReady, nextStaging);
  };

  const removeStaging = (id: string) => {
    const nextStaging = applyStaging((prev) => prev.filter((s) => s.id !== id));
    commit(ready, nextStaging);
  };

  const onPickFiles = (files: FileList | null) => {
    if (!files || files.length === 0 || !uploadAttachment) return;
    const picked: StagingEntry[] = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      status: "preparing",
    }));
    applyStaging((prev) => [...prev, ...picked]);
    for (const entry of picked) {
      void (async () => {
        try {
          const payload = await uploadAttachment(entry.file, {
            onProgress: (fraction) => {
              applyStaging((prev) =>
                prev.map((s) =>
                  s.id === entry.id && s.status === "preparing" ? { ...s, progress: fraction } : s,
                ),
              );
            },
          });
          const next = applyStaging((prev) =>
            prev.map((s) =>
              s.id === entry.id ? { id: s.id, file: s.file, status: "ready" as const, payload } : s,
            ),
          );
          commit(ready, next);
        } catch (err) {
          applyStaging((prev) =>
            prev.map((s) =>
              s.id === entry.id
                ? {
                    id: s.id,
                    file: s.file,
                    status: "error" as const,
                    error: err instanceof Error ? err.message : i18n._(msg`Caricamento fallito`),
                  }
                : s,
            ),
          );
        }
      })();
    }
  };

  // Debounced search of the Ando attachable-files index while the picker is
  // open. Aborts the in-flight request on a fresh keystroke / close so a slow
  // response can't overwrite newer intent (same race guard as RecipientsInput).
  React.useEffect(() => {
    if (!pickerOpen || !searchAttachableDocuments) return;
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      setPickerLoading(true);
      void searchAttachableDocuments({ query: pickerQuery, signal: ctrl.signal })
        .then((res) => setPickerResults(res))
        .catch(() => {
          /* aborted or failed — keep last results, never throw into render */
        })
        .finally(() => setPickerLoading(false));
    }, 220);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [pickerOpen, pickerQuery, searchAttachableDocuments]);

  // Focus the search input when the picker opens (replaces the autoFocus attr,
  // which biome flags — a11y: focus is intentional + scoped to user action).
  React.useEffect(() => {
    if (pickerOpen) pickerInputRef.current?.focus();
  }, [pickerOpen]);

  const handlePickDocument = (doc: AttachableDocumentOption) => {
    // Skip if this exact Ando doc is already attached (idempotent re-pick).
    if (ready.some((r) => r.content_item_id === doc.contentItemId)) {
      setPickerOpen(false);
      return;
    }
    const chip: AttachmentChipValue = {
      filename: doc.filename,
      mime_type: doc.mimeType ?? undefined,
      size_bytes: doc.sizeBytes ?? undefined,
      content_item_id: doc.contentItemId,
    };
    commit([...ready, chip], staging);
    setPickerOpen(false);
    setPickerQuery("");
  };

  const readOnly = disabled || (!uploadAttachment && !searchAttachableDocuments);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Persisted (AI-proposed or previously-added) attachments */}
      {ready.length === 0 && staging.length === 0 ? (
        <p className="text-[12px] text-ink-3">
          {readOnly
            ? i18n._(msg`Nessun allegato.`)
            : i18n._(msg`Nessun allegato — clicca per aggiungere.`)}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {ready.map((a) => (
            <li
              key={`r-${a.filename}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white/70 px-2 py-1 text-[11.5px] text-ink-2"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
              <span className="max-w-[180px] truncate font-medium text-ink-1">{a.filename}</span>
              {a.size_bytes ? (
                <span className="text-ink-3">{formatChipSize(a.size_bytes)}</span>
              ) : null}
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => removeReady(a.filename)}
                  aria-label={i18n._(msg`Rimuovi allegato ${a.filename}`)}
                  className="ml-0.5 grid h-4 w-4 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-black/[0.06] hover:text-ink-1"
                >
                  <X className="h-3 w-3" strokeWidth={2} aria-hidden />
                </button>
              ) : null}
            </li>
          ))}
          {/* Le entry ready restano in ``staging`` perché ``commit`` le usa per
              comporre il valore, ma sono già disegnate sopra da ``ready``. */}
          {staging
            .filter((s) => s.status !== "ready")
            .map((s) => (
            <li
              key={`s-${s.id}`}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border bg-white/70 px-2 py-1 text-[11.5px]",
                s.status === "error"
                  ? "border-destructive/40 text-destructive"
                  : "border-hairline text-ink-2",
              )}
            >
              {s.status === "preparing" ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-ink-3" aria-hidden />
              ) : s.status === "error" ? (
                <AlertCircle className="h-3 w-3 shrink-0 text-destructive" aria-hidden />
              ) : (
                <Paperclip className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
              )}
              <span className="max-w-[180px] truncate font-medium text-ink-1">{s.file.name}</span>
              <span className="text-ink-3">{formatChipSize(s.file.size)}</span>
              {s.status === "preparing" && typeof s.progress === "number" ? (
                <span className="text-[10px] text-ink-3 tabular-nums">
                  · {Math.round(s.progress * 100)}%
                </span>
              ) : null}
              {s.status === "error" ? (
                <span className="text-[10px]" title={s.error}>
                  · {i18n._(msg`errore`)}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => removeStaging(s.id)}
                aria-label={i18n._(msg`Rimuovi ${s.file.name}`)}
                className="ml-0.5 grid h-4 w-4 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-black/[0.06] hover:text-ink-1"
              >
                <X className="h-3 w-3" strokeWidth={2} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!readOnly ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {searchAttachableDocuments ? (
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                aria-expanded={pickerOpen}
                className={cn(
                  "inline-flex w-fit items-center gap-1.5 rounded-md border border-hairline bg-card px-2.5 py-1 text-[12px] text-ink-2",
                  "transition-colors hover:bg-white hover:text-ink-1",
                  pickerOpen && "bg-white text-ink-1",
                )}
              >
                <Search className="h-3 w-3" aria-hidden /> {i18n._(msg`Scegli dai documenti Ando`)}
              </button>
            ) : null}
            {uploadAttachment ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    onPickFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "inline-flex w-fit items-center gap-1.5 rounded-md border border-hairline bg-card px-2.5 py-1 text-[12px] text-ink-2",
                    "transition-colors hover:bg-white hover:text-ink-1",
                  )}
                >
                  <Paperclip className="h-3 w-3" aria-hidden />{" "}
                  {i18n._(msg`Carica file dal dispositivo`)}
                </button>
              </>
            ) : null}
          </div>

          {pickerOpen && searchAttachableDocuments ? (
            <div className="mt-1 rounded-lg border border-hairline bg-card p-2">
              <div className="flex items-center gap-2 rounded-md border border-hairline bg-white px-2 py-1">
                <Search className="h-3.5 w-3.5 text-ink-3" aria-hidden />
                <input
                  ref={pickerInputRef}
                  type="text"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder={i18n._(msg`Cerca un documento Ando…`)}
                  className="w-full bg-transparent text-[13px] text-ink-1 placeholder:text-ink-3 focus:outline-none"
                />
                {pickerLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-3" aria-hidden />
                ) : null}
              </div>
              <ul className="mt-1.5 max-h-56 space-y-0.5 overflow-y-auto">
                {pickerResults.length === 0 && !pickerLoading ? (
                  <li className="px-2 py-3 text-center text-[12px] text-ink-3">
                    {pickerQuery
                      ? i18n._(msg`Nessun documento trovato.`)
                      : i18n._(msg`Nessun documento disponibile.`)}
                  </li>
                ) : (
                  pickerResults.map((doc) => {
                    const already = ready.some((r) => r.content_item_id === doc.contentItemId);
                    return (
                      <li key={doc.contentItemId}>
                        <button
                          type="button"
                          disabled={already}
                          onClick={() => handlePickDocument(doc)}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px]",
                            "transition-colors hover:bg-ando-orange-soft disabled:cursor-default disabled:opacity-50",
                          )}
                        >
                          <Paperclip className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
                          <span className="min-w-0 flex-1 truncate text-ink-1">{doc.filename}</span>
                          {doc.sourceLabel ? (
                            <span className="shrink-0 text-[10px] text-ink-3">
                              {doc.sourceLabel}
                            </span>
                          ) : null}
                          {already ? (
                            <Check className="h-3 w-3 shrink-0 text-accent" aria-hidden />
                          ) : null}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
EmailAttachmentField.displayName = "EmailAttachmentField";
